import * as E from 'fp-ts/Either';
import * as t from 'io-ts';
import {appendContext} from 'io-ts';

import {reportWarn} from './ioTsLogger';
import {shouldDisableSafeMode} from './safeTypesHelpers';

/**
 * Function returning a custom combinator for ReadonlyArray.
 * If some of the items in the array cannot be validated, they are stripped from the array.
 * If the array is inside a union type, this codec will behave like `makeStrictArrayType`.
 * This is because union types attempt to decode every codec until one works.
 * @param valueCodec expect type inside the array
 * @param name Optional name of the codec
 * @returns A custom combinator for ReadonlyArrays
 */
export function makeSafeArrayType<C extends t.Mixed>(valueCodec: C, name?: string) {
  // eslint-disable-next-line rulesdir/avoid-io-ts-readonly-array
  const baseType = t.readonlyArray(valueCodec, name);

  return new t.ReadonlyArrayType(
    baseType.name,
    baseType.is,
    (unknownInput, context) => {
      const isStrict = shouldDisableSafeMode(context);
      // Default to the base implementation when strict mode is found in the context or if we're inside a union.
      if (isStrict) {
        return baseType.validate(unknownInput, context);
      }
      // "safe" implementation
      const e = t.UnknownArray.validate(unknownInput, context);
      if (E.isLeft(e)) {
        return e;
      }
      const validatedValues: Array<t.TypeOf<C>> = [];

      e.right.forEach((unknownValue, index) => {
        const result = valueCodec.validate(
          unknownValue,
          appendContext(context, String(index), valueCodec, unknownValue),
        );

        if (E.isLeft(result)) {
          result.left.forEach((error) => {
            reportWarn('SafeArrayType', error, baseType.name);
          });
          return;
        }

        validatedValues.push(result.right);
      });

      return t.success(Object.freeze(validatedValues));
    },
    baseType.encode,
    valueCodec,
  );
}

/**
 * Function returning a strict combinator for readonly arrays. If any item in the array fails to be parsed, decoding fails.
 * Use this when every item needs to be present for the data to be considered valid.
 * @param valueCodec expect type inside the array
 * @param name Optional name of the codec
 * @returns A strict combinator for ReadonlyArrays
 */
export function makeStrictArrayType<C extends t.Mixed>(valueCodec: C, name?: string) {
  // eslint-disable-next-line rulesdir/avoid-io-ts-readonly-array
  return t.readonlyArray(valueCodec, name);
}
